'use client';

import { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { BackendAuth } from '@/lib/auth-backend';

const OnboardingContext = createContext(null);

/**
 * FALLBACK onboarding task definitions.
 * Used only when no /_onboarding checklist exists in the database.
 * Tasks are now managed dynamically via /nexus/page-tours admin UI.
 */
export const ONBOARDING_TASKS = [
  {
    id: 'profile',
    translationKey: 'onboarding.task.profile',
    fallback: 'Profil vervollständigen',
    path: '/account/home/user-profile',
    icon: 'User',
  },
  {
    id: 'contacts',
    translationKey: 'onboarding.task.contacts',
    fallback: 'Erste Kontakte anlegen',
    path: '/contacts',
    icon: 'Users',
  },
  {
    id: 'team',
    translationKey: 'onboarding.task.team',
    fallback: 'Team-Mitglieder einladen',
    path: '/account/members/team-members',
    icon: 'UserPlus',
  },
  {
    id: 'communication',
    translationKey: 'onboarding.task.communication',
    fallback: 'Kommunikation einrichten',
    path: '/admin/communication-center/config',
    icon: 'Mail',
  },
  {
    id: 'calendar',
    translationKey: 'onboarding.task.calendar',
    fallback: 'Kalender erkunden',
    path: '/calendar',
    icon: 'Calendar',
  },
  {
    id: 'dashboard_customize',
    translationKey: 'onboarding.task.dashboard',
    fallback: 'Dashboard anpassen',
    path: '/admin/widgets',
    icon: 'LayoutDashboard',
  },
];

/**
 * OnboardingProvider
 * Manages state for the 3-layer onboarding system:
 * - Layer 1: Checklist widget (persisted in DB, tasks loaded dynamically)
 * - Layer 2: Hotspot beacons (persisted in localStorage)
 * - Layer 3: Module tours (persisted in localStorage)
 */
export function OnboardingProvider({ children }) {
  // Layer 1: Checklist widget
  const [isWidgetOpen, setIsWidgetOpen] = useState(false);
  const [checklistProgress, setChecklistProgress] = useState({});
  const [isDismissed, setIsDismissed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Dynamic tasks loaded from DB (or fallback)
  // null = not loaded yet, [] = loaded but empty (admin removed all), [...] = has tasks
  const [dynamicTasks, setDynamicTasks] = useState(null);
  const [taskSource, setTaskSource] = useState(null); // 'database', 'fallback', or null

  // Sequential checklists tracking
  const [currentTourId, setCurrentTourId] = useState(null);
  const [checklistName, setChecklistName] = useState(null);
  const [totalChecklists, setTotalChecklists] = useState(1);
  const [completedChecklists, setCompletedChecklists] = useState(0);
  const [allChecklistsCompleted, setAllChecklistsCompleted] = useState(false);

  // Layer 2: Hotspot "seen" tracking
  const [seenHotspots, setSeenHotspots] = useState({});

  // Layer 3: Tour "completed" tracking
  const [completedTours, setCompletedTours] = useState({});

  // Use dynamic tasks if loaded from DB, fallback only if DB fetch failed
  const tasks = useMemo(() => {
    // If we successfully loaded from DB (even if empty), use what we got
    if (taskSource === 'database') {
      return dynamicTasks || [];
    }
    // If DB fetch failed or still loading, use fallback
    if (dynamicTasks && dynamicTasks.length > 0) {
      return dynamicTasks;
    }
    return ONBOARDING_TASKS;
  }, [dynamicTasks, taskSource]);

  // Load from backend (checklist) and localStorage (hotspots, tours)
  useEffect(() => {
    loadTasks();
    loadProgress();
    loadLocalState();
  }, []);

  const loadTasks = async () => {
    try {
      // Get user's language from localStorage (set by i18n)
      const userLang = typeof window !== 'undefined'
        ? (localStorage.getItem('language') || 'de').split('_')[0].split('-')[0]
        : 'de';
      const response = await BackendAuth.apiCallJson(`/api/user/onboarding/tasks?lang=${userLang}`);
      if (response) {
        // Track the source: 'database' means admin has configured (even if empty)
        setTaskSource(response.source || 'fallback');

        // Track sequential checklist info
        setCurrentTourId(response.tour_id || null);
        setChecklistName(response.checklist_name || null);
        setTotalChecklists(response.total_checklists || 1);
        setCompletedChecklists(response.completed_checklists || 0);
        setAllChecklistsCompleted(response.all_completed || false);

        // Transform API response to match expected format
        const transformed = (response.tasks || []).map(task => ({
          id: task.id,
          translationKey: task.translationKey,
          fallback: task.fallback,
          label: task.label, // Pre-resolved label from backend
          path: task.path,
          icon: task.icon || 'CheckCircle',
        }));
        setDynamicTasks(transformed);

        // Clear task progress when loading a new checklist
        // (progress is per-checklist, not global)
        if (response.tour_id) {
          setChecklistProgress({});
        }
      }
    } catch {
      // Failed to load - will use fallback tasks
      setTaskSource('fallback');
    }
  };

  const loadProgress = async () => {
    try {
      const response = await BackendAuth.apiCallJson('/api/user/onboarding/progress');
      if (response) {
        setChecklistProgress(response.tasks || {});
        setIsDismissed(response.dismissed || false);
      }
    } catch {
      // First-time users won't have data - that's fine
    } finally {
      setIsLoading(false);
    }
  };

  const loadLocalState = () => {
    try {
      const hotspots = JSON.parse(localStorage.getItem('xoffi_seen_hotspots') || '{}');
      const tours = JSON.parse(localStorage.getItem('xoffi_completed_tours') || '{}');
      setSeenHotspots(hotspots);
      setCompletedTours(tours);
    } catch {
      // Ignore parse errors
    }
  };

  // --- Layer 1: Checklist methods ---

  const completeTask = useCallback(async (taskId) => {
    const updated = { ...checklistProgress, [taskId]: true };
    setChecklistProgress(updated);

    try {
      await BackendAuth.apiCallJson('/api/user/onboarding/progress', {
        method: 'PUT',
        body: JSON.stringify({ tasks: updated, dismissed: isDismissed }),
      });

      // Check if all tasks in current checklist are now completed
      const allTasksComplete = tasks.every(t => updated[t.id]);

      if (allTasksComplete && currentTourId) {
        // Mark this checklist as completed and load the next one
        try {
          await BackendAuth.apiCallJson(`/api/user/onboarding/checklist/${currentTourId}/complete`, {
            method: 'POST',
          });
          // Reload tasks to get the next checklist (or hide widget if all done)
          const tasksResponse = await BackendAuth.apiCallJson('/api/user/onboarding/tasks');
          if (tasksResponse) {
            const allDone = tasksResponse.all_completed || false;

            // Update state with new checklist data
            setTaskSource(tasksResponse.source || 'fallback');
            setCurrentTourId(tasksResponse.tour_id || null);
            setChecklistName(tasksResponse.checklist_name || null);
            setTotalChecklists(tasksResponse.total_checklists || 1);
            setCompletedChecklists(tasksResponse.completed_checklists || 0);
            setAllChecklistsCompleted(allDone);

            const transformed = (tasksResponse.tasks || []).map(task => ({
              id: task.id,
              translationKey: task.translationKey,
              fallback: task.fallback,
              label: task.label,
              path: task.path,
              icon: task.icon || 'CheckCircle',
            }));
            setDynamicTasks(transformed);
            setChecklistProgress({});

            // Return whether all checklists are now done
            // If allDone=true, widget should navigate (final task of final checklist)
            // If allDone=false, widget should NOT navigate (show next checklist)
            return { checklistCompleted: true, allDone };
          }
          return { checklistCompleted: true, allDone: false };
        } catch {
          // Silent - checklist completion failed but task progress is saved
        }
      }
    } catch {
      // Silent - local state is already updated
    }
    return { checklistCompleted: false, allDone: false };
  }, [checklistProgress, isDismissed, tasks, currentTourId]);

  const dismissWidget = useCallback(async () => {
    setIsDismissed(true);
    setIsWidgetOpen(false);
    try {
      await BackendAuth.apiCallJson('/api/user/onboarding/progress', {
        method: 'PUT',
        body: JSON.stringify({ tasks: checklistProgress, dismissed: true }),
      });
    } catch {
      // Silent
    }
  }, [checklistProgress]);

  const undismissWidget = useCallback(async () => {
    setIsDismissed(false);
    try {
      await BackendAuth.apiCallJson('/api/user/onboarding/progress', {
        method: 'PUT',
        body: JSON.stringify({ tasks: checklistProgress, dismissed: false }),
      });
    } catch {
      // Silent
    }
  }, [checklistProgress]);

  // --- Layer 2: Hotspot methods ---

  const markHotspotSeen = useCallback((hotspotId) => {
    const updated = { ...seenHotspots, [hotspotId]: true };
    setSeenHotspots(updated);
    localStorage.setItem('xoffi_seen_hotspots', JSON.stringify(updated));
  }, [seenHotspots]);

  const isHotspotSeen = useCallback((hotspotId) => {
    return !!seenHotspots[hotspotId];
  }, [seenHotspots]);

  // --- Layer 3: Tour methods ---

  const markTourCompleted = useCallback((tourId) => {
    const updated = { ...completedTours, [tourId]: true };
    setCompletedTours(updated);
    localStorage.setItem('xoffi_completed_tours', JSON.stringify(updated));
  }, [completedTours]);

  const isTourCompleted = useCallback((tourId) => {
    return !!completedTours[tourId];
  }, [completedTours]);

  const resetTour = useCallback((tourId) => {
    const updated = { ...completedTours };
    delete updated[tourId];
    setCompletedTours(updated);
    localStorage.setItem('xoffi_completed_tours', JSON.stringify(updated));
  }, [completedTours]);

  // --- Computed values ---

  const totalTasks = tasks.length;
  const completedCount = tasks.filter(t => checklistProgress[t.id]).length;
  const progressPercent = totalTasks > 0 ? Math.round((completedCount / totalTasks) * 100) : 0;
  const isAllComplete = completedCount === totalTasks && totalTasks > 0;

  // Show widget for users who haven't dismissed, have tasks to do, and haven't completed ALL checklists
  // Hide if: dismissed, no tasks, loading, or all checklists completed
  const shouldShowWidget = !isDismissed && !allChecklistsCompleted && !isLoading && totalTasks > 0;

  return (
    <OnboardingContext.Provider value={{
      // Layer 1
      isWidgetOpen, setIsWidgetOpen,
      checklistProgress, completeTask,
      isDismissed, dismissWidget, undismissWidget,
      totalTasks, completedCount, progressPercent, isAllComplete,
      shouldShowWidget,
      tasks, // Expose tasks for the widget
      // Sequential checklists
      checklistName,
      totalChecklists,
      completedChecklists,
      currentChecklist: completedChecklists + 1,
      allChecklistsCompleted,
      // Layer 2
      markHotspotSeen, isHotspotSeen,
      // Layer 3
      markTourCompleted, isTourCompleted, resetTour,
      // General
      isLoading,
    }}>
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding() {
  const ctx = useContext(OnboardingContext);
  if (!ctx) {
    throw new Error('useOnboarding must be used within OnboardingProvider');
  }
  return ctx;
}
